import { signal, useSignal } from '@preact/signals'
import { ImageObject } from 'apps/commerce/types.ts'
import { useOffer } from '$store/sdk/useOffer.ts'
import { getCheapestVariantOffers } from '$store/utils/getCheapestVariantOffers.ts'
import { formatPrice } from '$store/sdk/format.ts'
import Icon from '$store/components/ui/Icon.tsx'
import AddToCartButton from '$store/islands/AddToCartButton.tsx'
import Sizes from '$store/components/product/Sizes.tsx'
import { useVariantPossibilities } from '$store/sdk/useVariantPossibilities.ts'
import Similars from 'site/components/product/ProductSimilars.tsx'
import { SimilarProduct, useSimilars } from '$store/sdk/useSimilars.ts'
import { useId } from '$store/sdk/useId.ts'
import Slider from '$store/components/ui/Slider.tsx'
import SliderJS from '$store/islands/SliderJS.tsx'
import Image from 'apps/website/components/Image.tsx'
import c from '$store/sdk/c.ts'
import WishlistButton from '$store/components/wishlist/WishlistButton.tsx'
import { createRef } from 'preact'
import { SIZE_ORDER } from 'site/utils/sizeOrder.ts'
import type { SlimProduct } from 'site/types.ts'

interface ModalProduct extends SlimProduct {
    pixDiscount?: number
}

interface ModalBuyTriggerProps {
    product: ModalProduct
}

const selectedProduct = signal<ModalProduct | null>(null)
const selectedProductSimilars = signal<SimilarProduct[] | null>(null)
const selectedSkuId = signal<string | null>(null)

const useModalBuy = () => {
    return {
        selectedProduct,
        selectedProductSimilars,
        selectedSkuId,
    }
}

interface ModalBuyProductImagesProps {
    images: ImageObject[]
}

const ModalBuyProductImages = ({ images }: ModalBuyProductImagesProps) => {
    if (!images?.length) return null
    const id = useId()

    return (
        <div id={id} class='px-3 min-h-[205px]'>
            <div class='flex items-center relative'>
                <Slider class='carousel flex w-full'>
                    {images.map((image, index) => {
                        if (!image.url) return null

                        return (
                            <Slider.Item
                                class='carousel-item px-1 shrink-0 overflow-hidden'
                                index={index}
                            >
                                <Image src={image.url} width={163} height={203} />
                            </Slider.Item>
                        )
                    })}
                </Slider>

                <div class='flex justify-center items-center absolute left-[-25px]'>
                    <Slider.PrevButton class='p-3' disabled>
                        <Icon
                            width={12}
                            height={12}
                            id='ChevronLeft'
                            strokeWidth={3}
                            class='text-black'
                        />
                    </Slider.PrevButton>
                </div>
                <div class='flex justify-center items-center absolute right-[-25px]'>
                    <Slider.NextButton class='p-3' disabled>
                        <Icon
                            width={12}
                            height={12}
                            id='ChevronRight'
                            strokeWidth={3}
                            class='text-black'
                        />
                    </Slider.NextButton>
                </div>
            </div>
            <SliderJS rootId={id} />
        </div>
    )
}

export const ModalBuy = () => {
    const { selectedProduct, selectedSkuId } = useModalBuy()
    const modalRef = createRef()

    const currentProduct = selectedProduct.value

    const handleCloseModal = () => {
        selectedProduct.value = null
        selectedSkuId.value = null
        document.documentElement.classList.remove('overflow-hidden')
    }

    const isOpen = selectedProduct.value !== null

    const handleOpenModal = () => {
        document.documentElement.classList.add('overflow-hidden')
    }

    if (isOpen) {
        handleOpenModal()
    }

    const {
        image: images,
        offers,
        isVariantOf,
        url,
    } = currentProduct || {}

    const offersToDisplay = selectedSkuId.value && isVariantOf
        ? isVariantOf.hasVariant.find(({ sku }) => sku === selectedSkuId.value)?.offers
        : isVariantOf
        ? getCheapestVariantOffers(isVariantOf.hasVariant)
        : offers

    const {
        listPrice,
        price,
        seller,
        bestInstallment: installment,
    } = useOffer(offersToDisplay)

    const possibilities = currentProduct && useVariantPossibilities(currentProduct)
    const hasTriedToBuy = useSignal(false)

    const pixDiscount = currentProduct?.pixDiscount

    const isAvailable = isVariantOf?.hasVariant.some((variant) => {
        return variant.offers?.offers.some((offer) => (offer.inventoryLevel.value ?? 0) > 0)
    })

    const options = Object.entries(possibilities?.TAMANHO ?? possibilities?.Tamanho ?? {})
        .sort(([a], [b]) => a.localeCompare(b.padStart(2, '0')))
        .sort(([a], [b]) => SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b))
    const availableOptions = options?.filter(([, info]) => info.isAvailable)
    const hasVariants = availableOptions?.length > 1
    const parsedUrl = url?.split('?').shift()

    const similars = selectedProductSimilars.value

    return (
        <div>
            {isOpen && (
                <div class='fixed inset-0 bg-black opacity-50 z-[60]' onClick={handleCloseModal}>
                </div>
            )}
            <div
                class={c(
                    'fixed left-0 bottom-0 z-[70] w-full duration-300 lg:hidden',
                    isOpen && 'translate-y-0',
                    !isOpen && 'translate-y-full',
                )}
            >
                {currentProduct && (
                    <div
                        ref={modalRef}
                        class='scroll-height relative w-full px-2 pt-12 pb-7 bg-white grid'
                    >
                        <ModalBuyProductImages images={images ?? []} />
                        <div class='w-full max-w-[95vw]'>
                            <div class='flex items-start'>
                                <div class='flex flex-col md:gap-0 flex-1 relative px-3 pt-1'>
                                    {isAvailable
                                        ? (
                                            <div class='flex flex-col gap-1'>
                                                <div class='flex items-center gap-x-2 flex-row'>
                                                    {listPrice && price && listPrice > price && (
                                                        <div class='text-light text-sm text-neutral-2 line-through'>
                                                            {formatPrice(listPrice)}
                                                        </div>
                                                    )}
                                                    <div class='font-extrabold font-lato text-lg text-primary-700'>
                                                        {price && formatPrice(price)}
                                                    </div>
                                                </div>

                                                {installment?.billingIncrement &&
                                                    installment.billingDuration &&
                                                    installment.billingDuration > 1 && (
                                                    <div class='text-neutral-1 flex items-center gap-2 text-sm'>
                                                        <Icon id='Card' width={21} height={15} />
                                                        <div>
                                                            <span class='font-semibold'>
                                                                Parcele
                                                            </span>{' '}
                                                            <strong>
                                                                {installment.billingDuration}x
                                                            </strong>{' '}
                                                            de{' '}
                                                            <strong>
                                                                {formatPrice(
                                                                    installment.billingIncrement,
                                                                )}
                                                            </strong>
                                                        </div>
                                                    </div>
                                                )}
                                                {(!!pixDiscount && pixDiscount > 0) && (
                                                    <div class='text-neutral-1 text-sm'>
                                                        <span>ou</span>{' '}
                                                        <strong>{pixDiscount}%</strong>{' '}
                                                        <strong>de desconto no Pix</strong>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                        : (
                                            <div class='flex flex-col gap-2 font-lato text-neutral-1'>
                                                <span class='text-lg font-extrabold'>
                                                    Produto Indisponível
                                                </span>
                                                <a
                                                    href='#/'
                                                    class='font-semibold text-sm hover:underline'
                                                >
                                                    Avise-me quando estiver disponível!
                                                </a>
                                            </div>
                                        )}
                                </div>

                                <div class='flex items-center mt-2'>
                                    <a class='bg-primary-700 flex mr-1' href={parsedUrl}>
                                        <span class='text-white font-medium text-sm p-2'>
                                            Ver Descrição
                                        </span>
                                    </a>
                                    <WishlistButton
                                        productGroupID={currentProduct.isVariantOf?.productGroupID}
                                        productID={currentProduct.productID}
                                        layout='buyModal'
                                    />
                                </div>
                            </div>

                            {similars && similars.length > 0 && (
                                <div class='min-h-[65px] mt-4 max-w-[95%] pl-[10px]'>
                                    <span class='block mb-1 text-[12px] text-grey-800'>
                                        Outras opções de cores:
                                    </span>
                                    <Similars similars={similars} layout='buyModal' />
                                </div>
                            )}

                            <div
                                class='p-3'
                                style={{
                                    display:
                                        (currentProduct.isVariantOf?.hasVariant ?? []).length < 1
                                            ? 'none'
                                            : undefined,
                                }}
                            >
                                <Sizes
                                    hasVariants={hasVariants}
                                    options={availableOptions}
                                    product={currentProduct}
                                    selectedSkuIdSignal={selectedSkuId}
                                    hasTriedToBuy={hasTriedToBuy}
                                    layout='buyModal'
                                />

                                <div class='relative'>
                                    <AddToCartButton
                                        variant='modalBuy'
                                        item_list_id='modal_buy'
                                        item_list_name='Modal de Compra'
                                        class='addToShelf mt-3 disabled:opacity-50'
                                        disabled={!isAvailable || !selectedSkuId.value}
                                        itemsToAdd={[
                                            {
                                                skuId: selectedSkuId.value ?? '',
                                                brand: currentProduct.brand,
                                                category: currentProduct.category ?? '',
                                                productGroupID: currentProduct.isVariantOf
                                                    ?.productGroupID,
                                                productGroupName: currentProduct.isVariantOf?.name,
                                                listPrice: listPrice ?? price ?? 0,
                                                productID: currentProduct?.productID,
                                                url: parsedUrl ?? '',
                                                sellerId: seller ?? '1',
                                                price: price ?? 0,
                                                discount: price && listPrice
                                                    ? listPrice - price
                                                    : 0,
                                                name: currentProduct.name ?? '',
                                                productGroupId:
                                                    currentProduct.isVariantOf?.productGroupID ??
                                                        '',
                                            },
                                        ]}
                                    >
                                        <Icon id='Bag' width={22} height={22} />
                                        Comprar
                                    </AddToCartButton>
                                    {!selectedSkuId.value && (
                                        <strong class='absolute left-0 bottom-[-20px] font-lato text-sm text-center text-neutral-2 data-[tried-to-buy=true]:text-primary-700'>
                                            Selecione o tamanho
                                        </strong>
                                    )}
                                </div>
                            </div>

                            <button class='absolute right-4 top-4' onClick={handleCloseModal}>
                                <Icon id='XMark' class='text-neutral-0' width={24} height={24} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export const ModalBuyTrigger = ({ product }: ModalBuyTriggerProps) => {
    const { selectedProduct, selectedSkuId } = useModalBuy()

    const handleClick = async (e: MouseEvent) => {
        e.preventDefault()
        selectedProduct.value = product
        selectedSkuId.value = null
        const similars = await useSimilars(product, true)
        selectedProductSimilars.value = similars ?? null
    }

    return (
        <button
            class='product-buy-button bg-success-700 rounded-[60px] w-[34px] h-[34px] flex items-center justify-center'
            onClick={handleClick}
            id={product.productID}
        >
            <Icon id='Bag' width={16} height={16} class='text-white' />
        </button>
    )
}
